import {
  ArrowRight,
  ShieldCheck,
  HeartPulse,
  TrendingUp,
  Lock,
  Workflow,
  Building2,
  Layers,
} from "lucide-react";
import useDocTitle from "../lib/useDocTitle";
import Reveal from "../components/Reveal";
import ComplianceDisclaimer from "../components/ComplianceDisclaimer";

const products = [
  {
    icon: ShieldCheck,
    title: "Appeal Architect",
    status: "Available",
    statusColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    description:
      "Diagnostic and workflow software for e-commerce sellers managing marketplace compliance requirements across 18 platforms.",
    cta: "Visit appealarchitect.com",
    href: "https://appealarchitect.com",
    testId: "home-product-appeal-architect",
  },
  {
    icon: HeartPulse,
    title: "BlendSafe",
    status: "Available",
    statusColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    description:
      "Consumer reference tool that returns a clear SAFE / CAUTION / AVOID verdict on skincare-medication compatibility — backed by FDA labels and peer-reviewed dermatology research.",
    cta: "Visit blendsafe.com",
    href: "https://www.blendsafe.com",
    testId: "home-product-medskin",
  },
  {
    icon: TrendingUp,
    title: "PrimePillar",
    status: "Available",
    statusColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    description:
      "Micro-SaaS opportunity scanner that turns publicly available demand signals from seven sources into evidence-linked, AI-drafted product briefs.",
    cta: "Visit primepillar.io",
    href: "https://primepillar.io",
    testId: "home-product-paintoprofit",
  },
];

const values = [
  {
    icon: Lock,
    title: "Privacy-First Architecture",
    text: "We never request platform credentials, never automate against user accounts, and only collect data required to provide the service.",
  },
  {
    icon: Workflow,
    title: "Built for Real Operations",
    text: "Our products solve specific operational problems through structured workflows, not generic chatbots or one-size-fits-all advice.",
  },
  {
    icon: Building2,
    title: "US-Incorporated",
    text: "Lulo Software LLC is registered in Wyoming, USA, with direct customer support and transparent business practices.",
  },
  {
    icon: Layers,
    title: "Multi-Vertical Expertise",
    text: "Our portfolio spans e-commerce compliance, healthcare safety, and market intelligence software.",
  },
];

export default function Home() {
  useDocTitle(
    "Lulo Software · B2B SaaS for platforms, healthcare safety, and market intelligence",
    "Lulo Software LLC builds B2B SaaS tools across e-commerce compliance, healthcare safety, and market intelligence. Three products in our portfolio.",
    "B2B SaaS, e-commerce compliance, healthcare safety, market intelligence, Lulo Software, Wyoming SaaS company",
    "/",
  );

  return (
    <main data-testid="home-page" className="min-h-screen bg-white text-slate-900">
      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden border-b border-slate-200">
        {/* Navy-tinted geometric grid */}
        <div className="hero-grid absolute inset-0 pointer-events-none" aria-hidden />
        {/* Subtle navy → deeper-navy accent gradient (right side, very low alpha) */}
        <div
          aria-hidden
          className="absolute -top-32 -right-40 h-[620px] w-[620px] rounded-full blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(10,37,64,0.12), rgba(8,28,48,0.04) 60%, transparent 75%)",
          }}
        />
        {/* Hairline navy gradient bar at the very top */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(10,37,64,0.35), rgba(8,28,48,0.5), rgba(10,37,64,0.35), transparent)",
          }}
        />

        <div className="relative mx-auto max-w-7xl px-6 py-14 sm:py-24 lg:px-8 lg:py-36">
          <div className="max-w-4xl">
            <p className="lulo-anim-in mb-5 text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
              Lulo Software LLC · Wyoming, USA
            </p>

            <h1
              data-testid="home-hero-title"
              className="lulo-anim-in lulo-anim-delay-1 max-w-5xl text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5.25rem] font-bold tracking-tighter leading-[1.02] text-slate-950"
            >
              Decision-support software for professional teams.
            </h1>

            <p className="lulo-anim-in lulo-anim-delay-2 mt-8 max-w-3xl text-xl leading-8 text-slate-600">
              Lulo Software builds decision-support tooling across documentation
              workflows, skincare reference, and market research. Every product
              is designed for human-in-the-loop operational teams.
            </p>

            <div className="lulo-anim-in lulo-anim-delay-3 mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href="/products"
                data-testid="home-cta-products"
                className="inline-flex items-center justify-center rounded-xl bg-[#0A2540] px-6 py-4 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:bg-[#081c30] hover:shadow-md"
              >
                Explore Our Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>

              <a
                href="/about"
                data-testid="home-cta-about"
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-6 py-4 text-base font-semibold text-slate-900 transition-all duration-200 hover:border-slate-400 hover:bg-slate-50"
              >
                About Lulo Software
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — softened to remove marketplace/payment-processor brand callouts */}
      <section data-testid="home-trust" className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-6 text-center lg:px-8">
          <p className="text-base leading-7 text-slate-600">
            Built for businesses operating across major marketplaces and payment
            platforms — decision-support tooling for operational teams.
          </p>
        </div>
      </section>

      {/* PRODUCTS */}
      <section data-testid="home-products" className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
            What We Build
          </p>
          <h2 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
            Software for operational decisions.
          </h2>
          <p className="mt-6 text-lg leading-8 text-slate-600">
            We build B2B software that helps professionals make better
            operational decisions across e-commerce compliance, healthcare
            workflows, and market research.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {products.map((product, i) => {
            const Icon = product.icon;
            const isExternal = product.href.startsWith("http");

            return (
              <Reveal key={product.title} delay={i * 60}>
                <div
                  data-testid={product.testId}
                  className="group h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-200 ease-out hover:-translate-y-1.5 hover:border-blue-300 hover:shadow-xl hover:ring-1 hover:ring-blue-200/60"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-[#0A2540] transition-colors duration-200 group-hover:bg-blue-50 group-hover:text-blue-700">
                      <Icon className="h-6 w-6" />
                    </div>

                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${product.statusColor}`}
                    >
                      {product.status}
                    </span>
                  </div>

                  <h3 className="mt-8 text-2xl font-semibold tracking-tight text-slate-950">
                    {product.title}
                  </h3>

                  <p className="mt-4 min-h-[96px] text-base leading-7 text-slate-600">
                    {product.description}
                  </p>

                  <a
                    href={product.href}
                    target={isExternal ? "_blank" : "_self"}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    data-testid={`${product.testId}-cta`}
                    className="mt-8 inline-flex items-center text-base font-semibold text-blue-600 transition-all duration-200 hover:text-blue-700 group-hover:translate-x-0.5"
                  >
                    {product.cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* WHY LULO */}
      <section data-testid="home-why" className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
              Why Lulo Software
            </p>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
              Decision-support tooling, not decision-replacement.
            </h2>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {values.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={i * 50}>
                  <div
                    data-testid={`home-value-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="mb-6 flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-[#0A2540] shadow-sm">
                      <Icon className="h-5 w-5" />
                    </div>

                    <h3 className="text-lg font-semibold text-slate-950">
                      {item.title}
                    </h3>

                    <p className="mt-3 text-base leading-7 text-slate-600">
                      {item.text}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <ComplianceDisclaimer />
      <section data-testid="home-final-cta" className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="rounded-3xl border border-slate-200 bg-[#0A2540] p-10 text-white shadow-xl lg:p-14">
          <div className="grid gap-10 lg:grid-cols-[1.4fr_0.6fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-300">
                Corporate Software Portfolio
              </p>

              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                Built for businesses that need structured operational clarity.
              </h2>

              <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
                Explore Lulo Software&rsquo;s portfolio of compliance, safety,
                and market intelligence products.
              </p>
            </div>

            <div className="flex lg:justify-end">
              <a
                href="/contact"
                data-testid="home-final-cta-contact"
                className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-4 text-base font-semibold text-[#0A2540] transition hover:bg-slate-100"
              >
                Get in touch
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
